/**
 * PushRuleSearchPanel: "Find SKUs to add", the add half of the Skip or
 * Always 0 card.
 *
 * Results are grouped by product: one header row per product (its name, how
 * many SKUs it has, and [Skip] [Always 0] for the whole product), then the
 * matching SKUs under it, each named by its variant only ("S / Black", the
 * product's name is right above) with its own [Skip] [Always 0]. A client's
 * copies of its 3PL's garments have no product, so they group the same way by
 * base garment (the "3PL garment" badge marks the header; the buttons cover
 * every colour and size of it). There is no
 * "Add as" switch: the button IS the choice, so a row says what it will do.
 * A button for the rule a SKU already has is disabled and says so
 * ("Skipped", "Is Always 0").
 *
 * Search is a server query (20 rows), so the browser never loads the
 * catalogue. The host is a settings `<form>`: Enter in the box never submits
 * it. Enter moves to the results instead: to the one SKU when exactly one
 * matches (or a SKU matches the text exactly), else to the first result.
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import {
  SEARCH_MAX_LENGTH,
  SEARCH_MIN_LENGTH,
  groupSearchResults,
  variantLabel,
  groupTargetKey,
  type GroupTarget,
  type PushRuleSearchResult,
  type PushSkuRule,
} from '../utils/push-sku-rules';
import { RuleButton, SharedGarmentChip, SkuKindLabel } from './PushRuleBadge';

const SEARCH_DEBOUNCE_MS = 300;
/** The server returns at most this many search rows. */
const SEARCH_LIMIT = 20;
const INPUT_ID = 'push-rules-search';
const STATUS_ID = 'push-rules-search-status';

/** The change in flight from this panel, so its button can spin. */
export interface SearchPending {
  /** `sku:<id>`, `product:<id>` or `garment:<code>`. */
  key: string;
  rule: PushSkuRule;
}

interface PushRuleSearchPanelProps {
  /** Every stored rule by SKU id, so a row knows what it already has. */
  ruleBySkuId: ReadonlyMap<string, PushSkuRule>;
  busy: boolean;
  pending: SearchPending | null;
  onSku: (skuId: string, rule: PushSkuRule) => void;
  onGroup: (target: GroupTarget, rule: PushSkuRule) => void;
  onPaste: () => void;
  inputRef: RefObject<HTMLInputElement>;
}

export const PushRuleSearchPanel = memo(function PushRuleSearchPanel({
  ruleBySkuId,
  busy,
  pending,
  onSku,
  onGroup,
  onPaste,
  inputRef,
}: PushRuleSearchPanelProps) {
  const { t } = useTranslation('inventory');
  const [search, setSearch] = useState('');
  const typed = search.trim().slice(0, SEARCH_MAX_LENGTH);
  const term = useDebouncedValue(typed, SEARCH_DEBOUNCE_MS);
  const searchOn = term.length >= SEARCH_MIN_LENGTH;
  const searchQuery = trpc.stockSync.searchPushRuleSkus.useQuery(
    { q: term },
    { enabled: searchOn, placeholderData: keepPreviousData, staleTime: 30_000 },
  );
  const results = useMemo(() => (searchOn ? searchQuery.data?.results ?? [] : []), [searchOn, searchQuery.data]);
  const groups = useMemo(() => groupSearchResults(results), [results]);
  // A short or cleared box hides the old rows; typing on keeps them, dimmed.
  const searchStale = searchQuery.isFetching && searchQuery.isPlaceholderData;
  const fresh = searchOn && term === typed && !searchStale && !searchQuery.isLoading;

  const listRef = useRef<HTMLUListElement>(null);
  // Enter pressed before the results for the typed text arrived: move once they do.
  const enterPending = useRef(false);

  const focusResults = () => {
    const list = listRef.current;
    if (!list) return;
    const needle = typed.toUpperCase();
    const exact = results.find((result) => result.sku.toUpperCase() === needle);
    const target = exact ?? (results.length === 1 ? results[0] : undefined);
    const scope = target ? list.querySelector(`[data-sku-id="${target.id}"]`) : list;
    scope?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  };

  useEffect(() => {
    if (!enterPending.current || !fresh) return;
    enterPending.current = false;
    focusResults();
    // focusResults reads the same render's results; fresh is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh, results]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Never submit the settings form around this card.
      event.preventDefault();
      if (typed.length < SEARCH_MIN_LENGTH) return;
      if (fresh) focusResults();
      else enterPending.current = true;
      return;
    }
    if (event.key === 'Escape' && search !== '') {
      event.preventDefault();
      enterPending.current = false;
      setSearch('');
    }
  };

  let status = '';
  if (typed.length > 0 && typed.length < SEARCH_MIN_LENGTH) {
    status = t('stockSync.pushRules.search.minLength', { min: SEARCH_MIN_LENGTH });
  } else if (searchOn) {
    if (searchQuery.isLoading) status = t('stockSync.pushRules.search.searching');
    else if (!searchQuery.isError && results.length === 0 && !searchQuery.isFetching)
      status = t('stockSync.pushRules.search.noResults', { q: term });
    else if (results.length >= SEARCH_LIMIT) status = t('stockSync.pushRules.search.limited', { limit: SEARCH_LIMIT });
    else if (results.length > 0) status = t('stockSync.pushRules.search.found', { count: results.length });
  }

  /** `under`: the group header the row sits beneath (its name strips off the title), or null for a loose SKU. */
  const renderSkuRow = (result: PushRuleSearchResult, under: { name: string | null } | null) => {
    const underGroup = under !== null;
    const current = ruleBySkuId.get(result.id);
    const subtitle = underGroup ? variantLabel(result.title, under.name) : result.title;
    const key = `sku:${result.id}`;
    return (
      <li
        key={result.id}
        data-sku-id={result.id}
        className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 py-2 pr-3', underGroup ? 'pl-6' : 'pl-3')}
      >
        <div className="min-w-0 flex-1 basis-48">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="break-all font-mono text-xs">{result.sku}</span>
            <SkuKindLabel productionType={result.productionType} isBaseGarment={result.isBaseGarment} />
          </div>
          {subtitle && (
            <p className="truncate text-xs text-muted-foreground" title={subtitle}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap justify-end gap-1.5">
          {(['skip', 'always_zero'] as const).map((rule) => (
            <RuleButton
              key={rule}
              rule={rule}
              already={current === rule}
              pending={pending?.key === key && pending.rule === rule}
              disabled={busy}
              srContext={result.sku}
              onClick={() => onSku(result.id, rule)}
            />
          ))}
        </div>
      </li>
    );
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      {/* One measure for the label, the box and the results (DESIGN.md 10). */}
      <div className="max-w-prose space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor={INPUT_ID} className="text-sm font-medium">
            {t('stockSync.pushRules.search.label')}
          </Label>
          <Button type="button" variant="outline" className="h-8 gap-1.5 px-3 text-xs" onClick={onPaste}>
            <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
            {t('stockSync.pushRules.pasteList')}
          </Button>
        </div>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            ref={inputRef}
            id={INPUT_ID}
            type="search"
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(event) => {
              enterPending.current = false;
              setSearch(event.target.value);
            }}
            onKeyDown={onKeyDown}
            aria-describedby={STATUS_ID}
            placeholder={t('stockSync.pushRules.search.placeholder')}
            autoComplete="off"
            spellCheck={false}
            className="h-8 pl-8 text-sm"
          />
        </div>

        <p id={STATUS_ID} role="status" className="min-h-4 text-xs text-muted-foreground">
          {status}
        </p>

        {searchOn && searchQuery.isError && (
          <SectionError
            variant="inline"
            title={t('stockSync.pushRules.search.errorTitle')}
            body={t('stockSync.pushRules.search.errorBody')}
            retryLabel={t('stockSync.pushRules.retry')}
            retrying={searchQuery.isFetching}
            onRetry={() => void searchQuery.refetch()}
          />
        )}

        {searchOn && searchQuery.isLoading && (
          <div className="space-y-1.5" aria-hidden="true">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {groups.length > 0 && (
          <ul
            ref={listRef}
            aria-label={t('stockSync.pushRules.search.resultsLabel')}
            className={cn(
              'max-h-96 divide-y overflow-y-auto rounded-md border bg-background',
              searchStale && 'opacity-60',
            )}
          >
            {groups.map((group) => {
              const { product, garment } = group;
              if (!product && !garment) return renderSkuRow(group.results[0]!, null);
              const name = product ? (product.title ?? t('stockSync.pushRules.product.untitled')) : garment!.name;
              const skuCount = product ? product.skuCount : garment!.skuCount;
              const target: GroupTarget = product
                ? { kind: 'product', id: product.id }
                : { kind: 'garment', code: garment!.code };
              const key = groupTargetKey(target);
              return (
                <li key={key}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-muted/30 px-3 py-2">
                    <div className="min-w-0 flex-1 basis-48">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <p className="truncate text-sm font-medium" title={name}>
                          {name}
                        </p>
                        {garment && <SharedGarmentChip />}
                      </div>
                      <p className="text-xs tabular-nums text-muted-foreground">
                        {t('stockSync.pushRules.search.productSkus', { count: skuCount })}
                      </p>
                    </div>
                    <div className="ml-auto flex shrink-0 flex-wrap justify-end gap-1.5">
                      {(['skip', 'always_zero'] as const).map((rule) => (
                        <RuleButton
                          key={rule}
                          rule={rule}
                          pending={pending?.key === key && pending.rule === rule}
                          disabled={busy}
                          srContext={t('stockSync.pushRules.search.productContext', { product: name })}
                          onClick={() => onGroup(target, rule)}
                        />
                      ))}
                    </div>
                  </div>
                  <ul className="divide-y border-t">
                    {group.results.map((result) => renderSkuRow(result, { name: product ? product.title : garment!.name }))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
});
